import { Fragment, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { num, type Band } from '../lib/format';

/**
 * The pieces a data-heavy module is built from: cards, figures, status chips,
 * tables, ranked bar lists and a drill-in drawer.
 *
 * PROMOTED FROM `pages/noc/nocUi.tsx`, unchanged in behaviour, when the Admin
 * module needed the same set. They were written for the NOC alert library and
 * were generic from the start — a card is a card — so the alternative was a
 * second copy of four hundred lines, which is the thing migrations 021 and 043
 * exist to argue against. The CSS moved with them, from `noc-*` to `ui-*`.
 *
 * Two rules came across from the app the NOC library was moved in from, because
 * they are why it read well, and they now bind both modules:
 *
 *   1. Every chart ships a TABLE TWIN. The chart is the glance; the table is the
 *      values. Anything you can see you can also read, without hovering — which
 *      is also what makes the figures reachable by keyboard, by screen reader,
 *      and by anyone copying a number into a board pack.
 *   2. Status is ALWAYS glyph + label, with colour as the third channel. This
 *      brand has no green and no amber, so severity runs navy → crimson, and a
 *      reader who cannot separate those two must still get the message.
 */

/* ------------------------------------------------------------------ */
/* Cards                                                               */
/* ------------------------------------------------------------------ */

export function Card({
  title,
  subtitle,
  actions,
  children,
  className,
}: {
  title?: string;
  subtitle?: string;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={`card${className ? ` ${className}` : ''}`}>
      {(title || actions) && (
        <header className="card-head">
          <div>
            {title && <h2>{title}</h2>}
            {subtitle && <p className="sub">{subtitle}</p>}
          </div>
          {actions && <div className="row wrap">{actions}</div>}
        </header>
      )}
      <div className="card-body">{children}</div>
    </section>
  );
}

export interface TableSpec<T> {
  columns: Column<T>[];
  rows: T[];
  rowKey?: (row: T) => string;
  scroll?: boolean;
  empty?: string;
}

/**
 * A card whose contents can be read as a picture or as numbers.
 *
 * The toggle is not a convenience — see rule 1 above.
 */
export function ChartCard<T>({
  title,
  subtitle,
  table,
  children,
  footer,
  actions,
  className,
}: {
  title: string;
  subtitle?: string;
  table?: TableSpec<T>;
  children: ReactNode;
  footer?: string;
  /** Extra controls, shown before the chart/table toggle. */
  actions?: ReactNode;
  className?: string;
}) {
  const [view, setView] = useState<'chart' | 'table'>('chart');
  const id = useId();

  return (
    <Card
      className={className}
      title={title}
      subtitle={subtitle}
      actions={
        <>
          {actions}
          {table && (
            <div className="ui-toggle" role="group" aria-label={`${title} view`}>
              <button
                type="button"
                aria-pressed={view === 'chart'}
                onClick={() => setView('chart')}
                id={`${id}-chart`}
              >
                Chart
              </button>
              <button
                type="button"
                aria-pressed={view === 'table'}
                onClick={() => setView('table')}
                id={`${id}-table`}
              >
                Table
              </button>
            </div>
          )}
        </>
      }
    >
      {view === 'chart' || !table ? children : <DataTable {...table} />}
      {footer && <p className="sub mt-2">{footer}</p>}
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/* Figures                                                             */
/* ------------------------------------------------------------------ */

/**
 * The one number a page exists to answer.
 *
 * Given the navy surface so it is the one object on the page that is not a white
 * card — the same device the AM summary band uses.
 */
export function HeroFigure({
  label,
  value,
  unit,
  note,
}: {
  label: string;
  value: string;
  unit?: string;
  note?: string;
}) {
  return (
    <div className="ui-hero">
      <p className="label">{label}</p>
      <p className="figure">
        {value}
        {unit && <span className="unit">{unit}</span>}
      </p>
      {note && <p className="note">{note}</p>}
    </div>
  );
}

/** A supporting figure. This app's stat tile, unchanged. */
export function Stat({
  label,
  value,
  unit,
  sub,
  foot,
  flagged,
}: {
  label: string;
  value: string | number;
  unit?: string;
  /** A second figure under the headline one, quieter and smaller — the
   *  related number a reader wants next to it, not instead of it. */
  sub?: string;
  foot?: string;
  /** Draws the figure in the accent, for a count that means somebody has work
   *  to do. Never the only signal — the foot text says what it is. */
  flagged?: boolean;
}) {
  return (
    <div className="stat">
      <span className="label">{label}</span>
      <div className={`figure${flagged ? ' accent' : ''}`}>
        {value}
        {unit && <span className="ui-unit">{unit}</span>}
      </div>
      {sub && <span className="sub-figure">{sub}</span>}
      {foot && <span className="foot">{foot}</span>}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Status — colour never carries meaning alone                          */
/* ------------------------------------------------------------------ */

const GLYPH: Record<Band, string> = {
  good: '✓',
  warning: '!',
  serious: '!',
  critical: '✕',
  neutral: '·',
};

/**
 * Status as glyph + label on this app's badge ladder.
 *
 * The glyph is what makes `warning` and `serious` distinguishable at all: they
 * are two steps of the same crimson ramp, and the palette has no amber to put
 * between them.
 */
export function StatusChip({ band = 'neutral', children }: { band?: Band; children: ReactNode }) {
  return (
    <span className={`badge badge-${band}`}>
      <span className="ui-glyph" aria-hidden="true">
        {GLYPH[band]}
      </span>
      {children}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* Table                                                               */
/* ------------------------------------------------------------------ */

export interface Column<T> {
  key: string;
  label: string;
  align?: 'right';
  width?: string;
  render?: (row: T) => ReactNode;
}

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  scroll = true,
  empty = 'Nothing to show',
}: TableSpec<T>) {
  if (!rows?.length) return <p className="sub">{empty}</p>;
  return (
    <div className={`table-wrap${scroll ? ' ui-scroll' : ''}`}>
      <table className="table">
        <thead>
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                className={col.align === 'right' ? 'num' : undefined}
                style={col.width ? { width: col.width } : undefined}
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={rowKey ? rowKey(row) : i}>
              {columns.map((col) => (
                <td key={col.key} className={col.align === 'right' ? 'num' : undefined}>
                  {col.render
                    ? col.render(row)
                    : String((row as Record<string, unknown>)[col.key] ?? '')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Bar list — one hue, ranked                                          */
/* ------------------------------------------------------------------ */

/**
 * A ranked list of magnitudes, in HTML rather than SVG.
 *
 * One hue, not a categorical palette: these are the same measure for different
 * things, so colouring each row differently would imply a distinction that is
 * not there. Rank plus length carries it.
 *
 * Kept alongside `charts/RankedBars`, which does the same job in SVG. This one
 * is right where the rows carry rich labels and links; that one is right inside
 * a ChartCard beside other charts, where the marks should match.
 */
export function BarList({
  rows,
  color = 'var(--series-1)',
}: {
  rows: { name: string; sub?: string; value: number; display?: string; color?: string }[];
  color?: string;
}) {
  const max = Math.max(...rows.map((r) => r.value), 1);
  return (
    <div className="ui-bars">
      {rows.map((row) => (
        <div className="row" key={row.name}>
          <span className="name" title={row.name}>
            {row.name}
            {row.sub && <span className="ui-cell-sub"> · {row.sub}</span>}
          </span>
          <span className="track">
            {/* style rather than a fill attribute: var() resolves in a CSS
                property, not in a presentation attribute. */}
            <span
              className="fill"
              style={{
                /* Floored so a nonzero row is never invisible. */
                width: `${Math.max(1.5, (row.value / max) * 100)}%`,
                background: row.color ?? color,
              }}
            />
          </span>
          <span className="figure">{row.display ?? num(row.value)}</span>
        </div>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Small pieces                                                        */
/* ------------------------------------------------------------------ */

export function KeyValues({ items }: { items: [string, ReactNode][] }) {
  return (
    <dl className="ui-kv">
      {items.map(([k, v]) => (
        <Fragment key={k}>
          <dt>{k}</dt>
          <dd>{v}</dd>
        </Fragment>
      ))}
    </dl>
  );
}

/** A row of buttons behaving as one control — a filter or a range picker. */
export function Segmented<T extends string>({
  options,
  value,
  onChange,
  label,
}: {
  options: { id: T; label: string }[];
  value: T;
  onChange: (next: T) => void;
  label: string;
}) {
  return (
    <div className="ui-segmented" role="group" aria-label={label}>
      {options.map((o) => (
        <button key={o.id} type="button" aria-pressed={value === o.id} onClick={() => onChange(o.id)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Drawer — the drill-in surface                                        */
/* ------------------------------------------------------------------ */

/**
 * Slide-over panel for one record.
 *
 * A drawer rather than this app's `Modal` because of what goes in it: a document
 * to be read down, or a long form. A centred dialog sized for four fields would
 * spend the screen in the wrong direction.
 *
 * Escape closes, the backdrop closes, focus moves in and returns to wherever it
 * came from — the same contract Modal keeps.
 */
export function Drawer({
  open,
  onClose,
  title,
  subtitle,
  children,
  footer,
  wide,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  children: ReactNode;
  /** Pinned to the bottom of the panel — where a form's Save button belongs, so
   *  it is reachable without scrolling a long form back down. */
  footer?: ReactNode;
  wide?: boolean;
}) {
  const panel = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const previouslyFocused = document.activeElement;
    panel.current?.focus();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
      if (previouslyFocused instanceof HTMLElement) previouslyFocused.focus();
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="ui-drawer-root">
      <div className="backdrop" onMouseDown={onClose} role="presentation" />
      <div
        className={`panel${wide ? ' wide' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        ref={panel}
      >
        <header className="head">
          <div>
            <h2>{title}</h2>
            {subtitle && <p className="sub">{subtitle}</p>}
          </div>
          <button type="button" className="btn btn-ghost btn-icon" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </header>
        <div className="body">{children}</div>
        {footer && <div className="foot">{footer}</div>}
      </div>
    </div>
  );
}

/** A titled block inside a drawer. */
export function DrawerSection({
  title,
  count,
  children,
}: {
  title: string;
  count?: string;
  children: ReactNode;
}) {
  return (
    <section className="ui-drawer-section">
      <h3>
        {title}
        {count && <span className="count">{count}</span>}
      </h3>
      {children}
    </section>
  );
}
